import { ModalErrorIco, ModalInfoIcon, ModalSuccessIco } from '@modules/CustomIcons'
import { Modal, ModalFuncProps, Row, Spin, Typography } from 'antd'
import React, { useEffect, useState } from 'react'
import styles from './Dialog.module.scss'
import { QueryStatus } from '@reduxjs/toolkit/query'

export type Dialog = {
  open: boolean
  color?: string,
  loading?: boolean
  message?: React.ReactNode
  title: string
  modalType?: 'idle' | 'info' | 'loading' | 'success' | 'failed' | QueryStatus
} & ModalFuncProps

const renderIcon = (type: Dialog['modalType']) => {
  switch (type) {
    case 'loading':
      return <Spin size="large" />
    case QueryStatus.pending:
      return <Spin size="large" />
    case 'info':
      return <ModalInfoIcon />
    case 'success':
      return <ModalSuccessIco />
    case QueryStatus.fulfilled:
      return <ModalSuccessIco />
    case 'failed':
      return <ModalErrorIco />
    case QueryStatus.rejected:
      return <ModalErrorIco />
    default:
      return <ModalInfoIcon />
  }
}

const DialogComponent = function Dialog(props: Dialog) {
  const { color = '#007AFF', open, loading = false, message, title, modalType = 'idle', ...rest } = props
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setVisible(open)
  }, [open])

  useEffect(() => {
    if (!loading && visible) {
      const timer = setTimeout(() => {
        setVisible(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [loading, visible])

  return (
    <Modal
      className={`${styles.brDialog}`}
      title={title}
      footer={null}
      open={visible}
      centered
      closable={false}
      destroyOnClose
      style={{ minWidth: 250, maxWidth: 600 }}
      bodyStyle={{
        paddingBottom: 40,
        paddingTop: 40,
        textAlign: 'center',
        minHeight: 100,
      }}
      {...rest}
    >
      <Row
        align="middle"
        justify="center"
        style={{ flexDirection: 'column', height: '100%', color }}
      >
        {renderIcon(modalType)}
        {(modalType === QueryStatus.fulfilled ||
          modalType === QueryStatus.rejected ||
          modalType === 'success' ||
          modalType === 'info' ||
          modalType === 'failed') &&
          message && (
            <Typography.Text style={{ fontSize: 18, color: 'gray' }}>
              {message}
            </Typography.Text>
          )}
      </Row>
    </Modal>
  )
}

const ModalContent = ({
  title,
  color,
  modalType,
  message,
}: {
  modalType: Dialog['modalType']
  message: Dialog['message']
}) => (
  <>
    <div className="ant-modal-header">
      <div className="ant-modal-title">{title}</div>
    </div>
    <div className="ant-modal-content-inline">
      <Row
        align="middle"
        justify="center"
        gutter={[20, 20]}
        style={{ flexDirection: 'column', height: '100%', color }}
      >
        {renderIcon(modalType)}
        {(modalType === QueryStatus.fulfilled ||
          modalType === QueryStatus.rejected ||
          modalType === 'success' ||
          modalType === 'info' ||
          modalType === 'failed') &&
          message && <Typography.Paragraph style={{ fontSize: 15, fontWeight: 400, color: '#223142' }}>{message}</Typography.Paragraph>}
      </Row>
    </div>
  </>
)

DialogComponent.warning = ({
  modalType = 'failed',
  title,
  color,
  message,
  content,
  ...configs
}: Omit<Dialog, 'open'>) =>
  Modal.success({
    ...configs,
    icon: null,
    title: '',
    content: (
      <>
        <ModalContent color={color} title={title} message={message} modalType={modalType} />
      </>
    ),
    className: styles.brDialog,
    style: { maxWidth: 600 },
    centered: true,
    closable: false,
    bodyStyle: { textAlign: 'center', minHeight: 100 },
  })


DialogComponent.info = ({
  modalType = 'info',
  color = '#007AFF',
  title,
  message,
  content,
  ...configs
}: Omit<Dialog, 'open'>) =>
  Modal.info({
    ...configs,
    icon: null,
    title: '',
    content: (
      <>
        <ModalContent color={color} title={title} message={message} modalType={modalType} />
      </>
    ),
    className: styles.brDialog,
    style: { maxWidth: 600 },
    centered: true,
    closable: false,
    bodyStyle: { textAlign: 'center', minHeight: 100 },
  })

const Dialog = DialogComponent as typeof DialogComponent & {
  warning: (configs: Dialog) => void,
  info: (configs: Dialog) => void,
}

export default Dialog
